import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
} from '@mui/material';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ShareIcon from '@mui/icons-material/Share';
import TagIcon from '@mui/icons-material/Tag';
import WorkIcon from '@mui/icons-material/Work';
import {
  asArray,
  EmptyState,
  ErrorState,
  formatNumber,
  hasMeaningfulValue,
  LoadingState,
  MetricCard,
  OsintShell,
  pick,
  SUCCESS_GREEN,
  useOsintFetch,
  WARNING_AMBER,
} from './osintCommon';
import {
  formatDate,
  JobPostings,
  NewsArticles,
  SocialProfiles,
} from './osintRenderers';

const MediaSocialPage = () => {
  const { pentest_id } = useParams();
  const newsFetch = useOsintFetch('/pentests/osint/news', pentest_id, 'news');
  const jobsFetch = useOsintFetch('/pentests/osint/jobs', pentest_id, 'jobs');
  const socialFetch = useOsintFetch('/pentests/osint/social', pentest_id, 'social');

  if (newsFetch.loading || jobsFetch.loading || socialFetch.loading) return <LoadingState label="Loading media and social intelligence..." />;
  if (newsFetch.error) return <ErrorState message={newsFetch.error} />;
  if (jobsFetch.error) return <ErrorState message={jobsFetch.error} />;
  if (socialFetch.error) return <ErrorState message={socialFetch.error} />;

  const news = newsFetch.data || {};
  const jobs = jobsFetch.data || {};
  const social = socialFetch.data || {};
  const newsSummary = pick(news, ['summary'], {});
  const jobsSummary = pick(jobs, ['summary'], {});
  const articles = asArray(pick(news, ['articles', 'news_articles', 'items'], []));
  const postings = asArray(pick(jobs, ['postings', 'job_postings', 'jobs'], []));
  const articleCount = pick(newsSummary, ['article_count', 'articleCount'], articles.length);
  const openRoles = pick(jobsSummary, ['open_roles_count', 'openRolesCount'], pick(jobs, ['open_roles', 'openRoles'], postings.length));
  const topics = asArray(pick(newsSummary, ['topics'], pick(news, ['topics', 'summary_topics'], [])));
  const latestArticleDate = pick(newsSummary, ['latest_article_date', 'latestArticleDate'], pick(news, ['latest_article_date', 'latestArticleDate'], ''));
  const socialEntries = Object.entries(social || {}).filter(([, value]) => hasMeaningfulValue(value));

  return (
    <OsintShell>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Articles" value={formatNumber(articleCount)} helper="News articles collected from public sources." Icon={NewspaperIcon} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Open Roles" value={formatNumber(openRoles)} helper="Hiring demand and role count discovered publicly." Icon={WorkIcon} accent={SUCCESS_GREEN} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Social Platforms" value={formatNumber(socialEntries.length)} helper="Company social platforms with returned data." Icon={ShareIcon} accent="#7C3AED" /></Grid>
        <Grid item xs={12} sm={6} lg={3}><MetricCard label="Latest Article" value={latestArticleDate ? formatDate(latestArticleDate) : 'Unknown'} helper={`${topics.length} topic signal${topics.length === 1 ? '' : 's'} returned.`} Icon={TagIcon} accent={WARNING_AMBER} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <NewsArticles news={news} />
        </Grid>
        <Grid item xs={12}>
          <JobPostings jobs={jobs} />
        </Grid>
        <Grid item xs={12}>
          {socialEntries.length ? <SocialProfiles social={social} /> : <EmptyState title="No social media data returned" />}
        </Grid>
      </Grid>
    </OsintShell>
  );
};

export default MediaSocialPage;
